// "Import from document" modal. Two entry points, same shape as the
// repo-import modal:
//
//   1. Home / start-page — `mode = 'create-session'`. The shell creates
//      the session first, then calls onSubmit which posts to the
//      existing-session route. Same dance as the repo-import modal.
//
//   2. Inside an existing session — `mode = 'into-session'`. Calls
//      importFromDocument on the useSession hook directly. 409 = the
//      destructive-overwrite confirm path.
//
// File handling: user can either paste markdown / CSV directly or drop /
// select a file. We read the file as text client-side and feed the body
// to the server — keeps the upload path simple (no multipart) and lets
// the user preview / edit before submit.

import React, { useEffect, useRef, useState } from 'react';
import '../styles/ImportFromDocumentModal.css';

interface BaseProps {
  isOpen: boolean;
  onClose: () => void;
  // When set, the modal renders as a status display: skips the drop
  // zone / textarea, pre-fills the content, and auto-submits on open.
  // Used by the auto-route path (paperclip → structured doc detected)
  // so the user gets visible feedback during the import without
  // having to confirm anything (except destructive overwrite).
  presetContent?: { text: string; filename: string };
}

interface SubmitResult {
  ok: boolean;
  status?: number;
  error?: string;
  conflict?: { existingStatementCount: number };
}

interface CreateSessionProps extends BaseProps {
  mode: 'create-session';
  onSubmit: (text: string, opts: { filename: string }) => Promise<SubmitResult>;
}
interface IntoSessionProps extends BaseProps {
  mode: 'into-session';
  onSubmit: (text: string, opts: { filename: string; force: boolean }) => Promise<SubmitResult>;
}

type Props = CreateSessionProps | IntoSessionProps;

const ACCEPT = '.md,.markdown,.csv,.tsv,.txt,text/markdown,text/csv,text/tab-separated-values,text/plain';
const MAX_BYTES = 2 * 1024 * 1024; // 2 MB — well past the user's 120KB sample

const ImportFromDocumentModal: React.FC<Props> = (props) => {
  const { isOpen, onClose, presetContent } = props;
  const [text, setText] = useState('');
  const [filename, setFilename] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<{ existingStatementCount: number } | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isPreset = !!presetContent;

  useEffect(() => {
    if (!isOpen) return;
    setText(presetContent?.text ?? '');
    setFilename(presetContent?.filename ?? '');
    setError(null);
    setConflict(null);
    setBusy(false);
    setDragOver(false);
    if (!isPreset) {
      const t = setTimeout(() => textareaRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
    return undefined;
    // We deliberately exclude presetContent from deps: the modal owns
    // its own lifecycle once opened, and re-running this effect on
    // identity changes of the (object-literal) preset would re-trigger
    // auto-submit mid-flight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Auto-submit when opened with preset content. Fires once per
  // open-cycle; the busy guard plus the conflict/error early returns
  // prevent re-fires while the original call is in flight.
  const autoSubmitFiredRef = useRef(false);
  useEffect(() => {
    if (!isOpen) { autoSubmitFiredRef.current = false; return; }
    if (!isPreset) return;
    if (autoSubmitFiredRef.current) return;
    if (!presetContent?.text?.trim()) return;
    autoSubmitFiredRef.current = true;
    // Defer one tick so the modal renders the busy spinner BEFORE the
    // synchronous part of submit() runs — otherwise the user sees a
    // flash of the empty body.
    const t = setTimeout(() => { submit(false); }, 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isPreset]);

  if (!isOpen) return null;

  const ingestFile = async (file: File) => {
    if (file.size > MAX_BYTES) {
      setError(`File too large: ${formatSize(file.size)} (limit ${formatSize(MAX_BYTES)})`);
      return;
    }
    try {
      const body = await file.text();
      setText(body);
      setFilename(file.name);
      setError(null);
    } catch (err) {
      setError(`Failed to read file: ${(err as Error).message}`);
    }
  };

  const onFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) ingestFile(f);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (busy) return;
    const f = e.dataTransfer.files?.[0];
    if (f) ingestFile(f);
  };

  const submit = async (force = false) => {
    // Prefer presetContent over state so the auto-submit path works
    // even if React hasn't propagated the setText/setFilename calls
    // from the open-effect by the time setTimeout(0) fires (the
    // closure here would otherwise see the initial empty state).
    const body = (presetContent?.text ?? text).trim();
    const fn = presetContent?.filename ?? filename;
    if (!body) return;
    setBusy(true);
    setError(null);
    setConflict(null);

    try {
      const result = props.mode === 'create-session'
        ? await props.onSubmit(body, { filename: fn })
        : await props.onSubmit(body, { filename: fn, force });
      if (!result.ok) {
        if (result.status === 409 && result.conflict) {
          setConflict(result.conflict);
        } else {
          setError(result.error || 'Import failed.');
        }
        return;
      }
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="ifd-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="ifd-modal" role="dialog" aria-modal="true" aria-labelledby="ifd-title">
        <header className="ifd-head">
          <h2 id="ifd-title">Import from document</h2>
          <button className="ifd-close" onClick={onClose} disabled={busy} aria-label="Close">×</button>
        </header>

        <div className="ifd-body">
          {isPreset ? (
            <p className="ifd-blurb">
              Detected a structured table in <strong>{filename || 'the attachment'}</strong>.
              Importing as flow — each row becomes a statement, grouped by bounded context.
            </p>
          ) : (
            <p className="ifd-blurb">
              Drop a markdown table or CSV file, or paste the content below.
              Each row becomes a flow statement, grouped by bounded context.
            </p>
          )}

          {!isPreset && (
          <>
          <div
            className={`ifd-drop ${dragOver ? 'ifd-drop--over' : ''}`}
            onDragOver={(e) => {
              if (busy) return;
              if (e.dataTransfer.types.includes('Files')) {
                e.preventDefault();
                setDragOver(true);
              }
            }}
            onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOver(false); }}
            onDrop={onDrop}
          >
            <button
              type="button"
              className="ifd-pick-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
            >
              Choose file
            </button>
            <span className="ifd-drop-hint">
              {filename ? <strong>{filename}</strong> : 'or drag a .md / .csv / .tsv file here'}
            </span>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT}
              onChange={onFilePicked}
              style={{ display: 'none' }}
            />
          </div>

          <textarea
            ref={textareaRef}
            className="ifd-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape' && !busy) onClose();
            }}
            placeholder={
              '| Bounded Context | Feature | Code | Description | Present | Source |\n' +
              '|---|---|---|---|---|---|\n' +
              '| Sales | Lead Capture | LC-001 | Capture an MQL via webform | Y | Webform |\n' +
              '...'
            }
            spellCheck={false}
            disabled={busy}
            rows={10}
          />

          <p className="ifd-hint">
            We auto-detect the format and column roles (bounded context, feature
            name, code, description, presence, source). One row per feature is
            ideal; rows without a recognisable name are reported in open questions.
          </p>
          </>
          )}

          {conflict && (
            <div className="ifd-warning">
              <strong>This will replace your current flow.</strong> The session
              already has <strong>{conflict.existingStatementCount}</strong>{' '}
              statements. Continue and the imported flow will overwrite them.
              Chat history is preserved either way.
            </div>
          )}

          {error && <div className="ifd-error">{error}</div>}

          {busy && (
            <div className="ifd-busy">
              <span className="ifd-spinner" /> Parsing document, synthesising flow…
            </div>
          )}
        </div>

        <footer className="ifd-foot">
          <button className="ifd-btn ifd-btn--ghost" onClick={onClose} disabled={busy}>
            {isPreset && !conflict && !error ? 'Close' : 'Cancel'}
          </button>
          {conflict ? (
            <button
              className="ifd-btn ifd-btn--danger"
              onClick={() => submit(true)}
              disabled={busy}
            >
              Replace flow and import
            </button>
          ) : !isPreset ? (
            <button
              className="ifd-btn ifd-btn--primary"
              onClick={() => submit(false)}
              disabled={busy || !text.trim()}
            >
              {busy ? 'Importing…' : (props.mode === 'create-session' ? 'Create session' : 'Import')}
            </button>
          ) : null}
        </footer>
      </div>
    </div>
  );
};

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default ImportFromDocumentModal;
